import cx from "classnames";
import { Icon } from "common/Icon";
import { sharedDroppableStyle, sharedPlaceholderStyle } from "features/experiences/ExperiencesApp";
import p from "prop-types";
import React, { Fragment, useEffect } from "react";
import { Draggable, Droppable } from "react-beautiful-dnd";
import ReactDOM from "react-dom";
import { DeleteGuidePageDialogContainer } from "../../../dialogs/DeleteGuidePageDialog";
import { GuidePageViewer } from "../GuidePageViewer";
import styles from "./GuidePageListItem.scss";

export class GuidePageListItem extends React.PureComponent {
  static propTypes = {
    guide: p.object.isRequired,
  };

  static defaultProps = {
    editable: true,
  };

  constructor(props) {
    super(props);

    this.state = {
      showDeleteDialog: false,
    };
  }

  deleteClicked = () => {
    this.setState({ showDeleteDialog: true });
  };

  titleClicked = (e) => {
    const { expandedId, setExpanded, guide } = this.props;
    // Only accept clicks that weren't clicking on something else
    if (e.target === e.currentTarget) {
      // allow headers to collapse
      if (guide.isHeader) {
        expandedId === guide.id ? setExpanded(null) : setExpanded(guide.id);
      } else {
        expandedId !== guide.id && setExpanded(guide.id);
      }
    }
  };

  chevronClicked = (e) => {
    const { expandedId, setExpanded, guide } = this.props;
    expandedId === guide.id ? setExpanded(null) : setExpanded(guide.id);
  };

  hideDeleteDialog = () => {
    this.setState({ showDeleteDialog: false });
  };

  render() {
    const {
      draggingId,
      guides,
      guide,
      pendingPositions,
      childrenForId,
      expandedId,
      dragHandleProps,
      hoverDisabled,
      portalRef,
      isDragging,
      setExpanded,
      editable,
      nested,
    } = this.props;
    const { showDeleteDialog } = this.state;

    const childIds = childrenForId(guide.id).map(({ id }) => id);
    const expanded = expandedId === guide.id;
    const selfOrChildExpanded = expanded || childIds.includes(expandedId);
    const anyDragging = draggingId !== null;
    const renderHeaderDroppable = editable && guide.isHeader && (selfOrChildExpanded || anyDragging) && !isDragging;
    const draggingHeader = draggingId && guides.get(parseInt(draggingId, 10)).isHeader;

    return (
      <Fragment>
        <div
          className={cx(
            styles.GuideTitle,
            expanded && styles.active,
            isDragging && styles.isDragging,
            !hoverDisabled && styles.hoverEffects,
            editable && guide.isHeader && styles.header,
            nested && styles.nested
          )}
          onClick={this.titleClicked}
        >
          <div className={cx(styles.title, guide.isHeader && styles.header)} onClick={this.titleClicked}>
            {guide.isLive && <Icon size={20} i="ios-wifi" className={styles.rightMargin} />}
            {guide.title || "(Untitled)"}
            {editable && guide.isHeader && <span className={styles.header}>header</span>}
          </div>
          <div className={styles.controls}>
            {editable && (
              <div className={cx(styles.guideControls, (expanded || isDragging) && styles.showGuideControls)}>
                <Icon i="md-trash" size={18} className={styles.control} onClick={this.deleteClicked} />
                <Icon
                  i="md-move"
                  size={18}
                  className={cx(styles.control, expanded && styles.disabled)}
                  {...dragHandleProps}
                />
              </div>
            )}
            {guide.isHeader && (
              <div className={styles.expandIconContainer} onClick={this.chevronClicked}>
                <Icon
                  i="chevron-centered"
                  size={18}
                  className={cx(styles.control, styles.expandIcon, expanded && styles.expanded)}
                />
              </div>
            )}
          </div>
          {showDeleteDialog && (
            <DeleteGuidePageDialogContainer
              guide={guide}
              expanded={expanded}
              setExpanded={setExpanded}
              onRequestClose={this.hideDeleteDialog}
            />
          )}
        </div>

        {renderHeaderDroppable && (
          <HeaderDroppable
            draggingId={draggingId}
            draggingHeader={draggingHeader}
            guides={guides}
            isDragging={isDragging}
            anyDragging={anyDragging}
            expanded={expanded}
            expandedId={expandedId}
            selfOrChildExpanded={selfOrChildExpanded}
            setExpanded={setExpanded}
            subItems={childrenForId(guide.id)}
            pendingPositions={pendingPositions}
            type={guide.id}
            childrenForId={childrenForId}
            portalRef={portalRef}
          />
        )}

        {!editable &&
          selfOrChildExpanded &&
          (guide.isHeader ? (
            childrenForId(guide.id)
              .valueSeq()
              .map((g) => (
                <GuidePageListItem
                  key={`overviewGP-${g.id}`}
                  editable={false}
                  guides={guides}
                  guide={g}
                  childrenForId={childrenForId}
                  setExpanded={setExpanded}
                  expandedId={expandedId}
                />
              ))
          ) : (
            <div className={cx(styles.GuideContent, nested && styles.nestedContent)}>
              <GuidePageViewer guide={guide} />
            </div>
          ))}
      </Fragment>
    );
  }
}

export const HeaderDroppable = ({
  draggingId,
  draggingHeader,
  selfOrChildExpanded,
  guides,
  pendingPositions,
  isDragging,
  anyDragging,
  expanded,
  expandedId,
  subItems,
  type,
  childrenForId,
  setExpanded,
  portalRef,
}) => {
  const sortedSubItems = pendingPositions ? subItems.sortBy(({ id }) => pendingPositions.get(id)) : subItems;

  return (
    <Droppable droppableId={`${type}`} type="droppable">
      {(provided, snapshot) => {
        const { isDraggingOver } = snapshot;
        const showDraggables = selfOrChildExpanded || isDraggingOver;
        return (
          <div>
            <div
              ref={provided.innerRef}
              style={Object.assign(
                {
                  height: !showDraggables && "1px", // needs to be >0 in order to have something to drag over
                  ...sharedDroppableStyle(isDraggingOver),
                },
                draggingHeader && {
                  display: "none",
                }
              )}
            >
              {sortedSubItems.size > 0 &&
                sortedSubItems.valueSeq().map(({ id, _position }, index) => {
                  const guide = guides.get(id);
                  return (
                    showDraggables && (
                      <Draggable key={`${guide.id}`} draggableId={`${guide.id}`} index={index}>
                        {(provided, snapshot) => (
                          <div {...provided.draggableProps} ref={provided.innerRef}>
                            <WithPortal
                              node={
                                <GuidePageListItem
                                  nested
                                  setExpanded={setExpanded}
                                  guide={guide}
                                  guides={guides}
                                  childrenForId={childrenForId}
                                  expandedId={expandedId}
                                  isDragging={snapshot.isDragging}
                                  dragHandleProps={provided.dragHandleProps}
                                />
                              }
                              portalRef={portalRef}
                              snapshot={snapshot}
                            />
                          </div>
                        )}
                      </Draggable>
                    )
                  );
                })}
            </div>
            <div
              style={Object.assign(
                {
                  paddingBottom: isDraggingOver && "1px",
                  borderLeft: "2px",
                  borderRight: "2px",
                  ...sharedPlaceholderStyle(isDraggingOver),
                },
                draggingHeader && {
                  display: "none",
                }
              )}
            >
              {provided.placeholder}
            </div>
          </div>
        );
      }}
    </Droppable>
  );
};

export const WithPortal = ({ node, portalRef, snapshot }) => {
  const move = (e) => {
    if (portalRef) {
      portalRef.style.width = `${portalRef.parentElement.offsetWidth}px`;
      portalRef.style.top = `${e.pageY - 100}px`;
    }
  };

  useEffect(() => {
    window.addEventListener("mousemove", move);

    return () => {
      window.removeEventListener("mousemove", move);
    };
  }, [portalRef]);

  if (snapshot.isDragging) {
    return ReactDOM.createPortal(node, portalRef);
  }

  return node;
};
